/**
 * B629 (a) — a channel's fees and tax rates, set and read through their own
 * doors. Until this the pricing engine priced both (§10.3) and the only
 * writers were the dev/demo seeders. Replace-whole-list per channel; the
 * domain names every problem (a fee is an amount XOR a percentage — checked
 * there, not as a zod union, so every door including the MCP membrane
 * translates the flat shape). Amounts are minor units in the channel's
 * currency; percentages and rates are basis points.
 */
import { z } from "zod";
import { readOp, writeOp, zId } from "./common.js";

/** B1063 — what a buyer reads on a fee's line; the admin judges it at the field. */
export const FEE_NAME_MAX = 60;
/** B1063 — a tax category's name (products pick from these). */
export const TAX_CATEGORY_MAX = 60;
/** B1063 — fees one channel charges; the admin's Add door stops here. */
export const PRICING_FEES_MAX = 20;
/** B1063 — tax rates one channel keeps. */
export const PRICING_TAX_RATES_MAX = 50;

const zFee = z
  .object({
    feeId: zId,
    /** What the buyer reads on the line — "Service fee". */
    name: z.string().min(1).max(FEE_NAME_MAX),
    scope: z.enum(["PER_LINE", "PER_ORDER"]),
    bearer: z.enum(["CUSTOMER", "TENANT"]),
    amount: z.number().int().optional(),
    percentBps: z.number().int().optional(),
    /** B629 (d) — false keeps the fee on a refund; absent = given back in proportion (the default). */
    refundable: z.boolean().optional(),
  })
  .strict();

const zTaxRate = z
  .object({
    category: z.string().min(1).max(TAX_CATEGORY_MAX),
    rateBps: z.number().int(),
    inclusive: z.boolean(),
  })
  .strict();

const zChannelPricing = z
  .object({
    channelId: zId,
    /** The channel's price-list currency — what a fee's `amount` is counted in. */
    currency: z.string().min(3).max(3).optional(),
    fees: z.array(zFee),
    taxRates: z.array(zTaxRate),
    /** Categories the channel's products on sale are in — each needs a rate. */
    taxCategoriesInUse: z.array(z.string()),
    /** B1077 — the channel's fees a live promotion waives (the fee the set refuses to drop), and the promotion's name. */
    feesWaived: z.array(z.object({ feeId: zId, promotion: z.string() }).strict()).optional(),
  })
  .strict();

const EXAMPLE_ROW = {
  channelId: "web",
  currency: "USD",
  fees: [{ feeId: "service", name: "Service fee", scope: "PER_ORDER" as const, bearer: "CUSTOMER" as const, amount: 400 }],
  taxRates: [{ category: "standard", rateBps: 860, inclusive: false }],
  taxCategoriesInUse: ["standard"],
};

export const pricingGet = readOp({
  name: "pricing.get",
  permission: "catalog:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z.object({ channels: z.array(zChannelPricing) }).strict(),
  exampleRequest: {},
  exampleResponse: { channels: [EXAMPLE_ROW] },
});

export const pricingFeesSet = writeOp({
  name: "pricing.fees.set",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { channelId: zId, fees: z.array(zFee).max(PRICING_FEES_MAX) },
  response: zChannelPricing,
  exampleRequest: { channelId: "web", fees: EXAMPLE_ROW.fees },
  exampleResponse: EXAMPLE_ROW,
});

export const pricingTaxesSet = writeOp({
  name: "pricing.taxes.set",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { channelId: zId, taxRates: z.array(zTaxRate).max(PRICING_TAX_RATES_MAX) },
  response: zChannelPricing,
  exampleRequest: { channelId: "web", taxRates: EXAMPLE_ROW.taxRates },
  exampleResponse: EXAMPLE_ROW,
});
